import React, { useState } from 'react';
import { X } from 'lucide-react';
import EditMetricModal from './EditMetricModal';
import DeleteMetricDialog from '@/components/common/DeleteMetricDialog';
import ExtractMetricsModal from './modals/ExtractMetricsModal';
import { toast } from 'sonner';
import { deletePricingMetric, addMetric } from '@/utils/metricsUtils';

// Types
export interface Metric {
  id: string;
  displayName: string;
  modality: string;
  [key: string]: any;
}

interface DeleteModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  metric: Metric | null;
  onDeleted: () => void;
}

interface EditModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  metric: Metric | null;
  onUpdated: (metric: Metric) => void;
}

interface AddModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  slug: string;
  onAdded: () => void;
}

interface ExtractModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  slug: string;
  subslug?: string;
}

// Delete Modal Component
export function DeleteMetricModal({ isOpen, setIsOpen, metric, onDeleted }: DeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle confirming the deletion
  const handleConfirmDelete = async () => {
    if (!metric?.id) return;
    
    setIsDeleting(true);
    
    try {
      // Call the delete function
      const success = await deletePricingMetric(metric.id);
      
      if (success) {
        toast("Metric deleted successfully");
        onDeleted();
      } else {
        toast.error("Failed to delete metric");
      }
    } catch (error) {
      console.error("Error deleting metric:", error);
      toast.error("Error deleting metric");
    } finally {
      setIsDeleting(false);
      setIsOpen(false);
    }
  };

  return (
    <DeleteMetricDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      metricName={metric?.displayName || ''}
      metricId={metric?.id || ''}
      isDeleting={isDeleting}
      onConfirmDelete={handleConfirmDelete}
    />
  );
}

// Edit Modal Component
export function EditMetricModalWrapper({ isOpen, setIsOpen, metric, onUpdated }: EditModalProps) {
  return (
    <EditMetricModal
      metric={metric}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onSuccess={onUpdated}
    />
  );
}

// Add Metric Modal Component
export function AddMetricModal({ isOpen, setIsOpen, slug, onAdded }: AddModalProps) {
  const [newMetricName, setNewMetricName] = useState('');
  const [newMetricModality, setNewMetricModality] = useState<'Flat' | 'License' | 'Credit' | 'Usage' | 'Threshold' | 'Unknown'>('Usage');
  const [isAddingMetric, setIsAddingMetric] = useState(false);

  // Handle adding a new metric
  const handleAddMetric = async () => {
    if (!newMetricName.trim() || !slug) return;
    
    setIsAddingMetric(true);
    
    try {
      const result = await addMetric({
        slug,
        displayName: newMetricName,
        modality: newMetricModality
      });
      
      if (result.success) {
        toast("Metric added successfully");
        setNewMetricName('');
        setIsOpen(false);
        onAdded();
      } else {
        toast.error("Failed to add metric");
      }
    } catch (error) {
      console.error("Error adding metric:", error);
      toast.error("Error adding metric");
    } finally {
      setIsAddingMetric(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button 
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
        
        <h2 className="text-xl font-semibold mb-4">Add New Metric</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metric Name</label>
            <input
              type="text"
              value={newMetricName}
              onChange={(e) => setNewMetricName(e.target.value)}
              placeholder="Enter metric name"
              className="w-full p-2 border rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
            <select
              value={newMetricModality}
              onChange={(e) => setNewMetricModality(e.target.value as any)}
              className="w-full p-2 border rounded-md bg-white"
            >
              <option value="Usage">Usage</option>
              <option value="License">License</option>
              <option value="Credit">Credit</option>
              <option value="Flat">Flat</option>
              <option value="Threshold">Threshold</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
          
          <div className="pt-4 border-t flex justify-end space-x-3">
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMetric}
              disabled={!newMetricName.trim() || isAddingMetric}
              className={`px-4 py-2 rounded-md bg-blue-600 text-white ${!newMetricName.trim() || isAddingMetric ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
            >
              {isAddingMetric ? 'Adding...' : 'Add Metric'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Extract Metrics Modal Component
export function ExtractMetricsModalWrapper({ isOpen, setIsOpen, slug, subslug }: ExtractModalProps) {
  return (
    <ExtractMetricsModal
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      slug={slug}
      subslug={subslug}
    />
  );
}
